import { useEffect, useRef } from "react";
import type { KeyState } from "../wordle/keystate";
import { FlapTile } from "./FlapTile";

const CELLS = 11;
/** What the strip's modules rattle through: it speaks Ukrainian and shows latin answers. */
const STRIP_ALPHABET = "АБВГДЕЄЖЗИІЇЙКЛМНОПРСТУФХЦЧШЩЮЯ0123456789";
/** Letter-by-letter roll along the strip. */
const STEP_MS = 55;

/**
 * What the strip says: tries left while playing ("остання" in amber on the
 * last one), the praise line on a win, the answer in amber on a loss.
 */
export function stripMessage(
  status: "playing" | "won" | "lost",
  attempts: number,
  winLines: readonly string[],
  answer: string | undefined,
): { text: string; tone: KeyState | null } {
  if (status === "won") return { text: winLines[attempts - 1] ?? "Є", tone: "g" };
  if (status === "lost") return { text: `слово ${answer ?? ""}`.trim(), tone: "y" };
  const left = 6 - attempts;
  return left === 1 ? { text: "остання", tone: "y" } : { text: `спроб ${left}`, tone: null };
}

interface FlapStripProps {
  text: string;
  /** Colour of the cells that carry text: plain board, lit green (win) or amber (warning / answer). */
  tone: KeyState | null;
  maxWidth: string;
}

/**
 * The departures-board line above the grid ("СПРОБ 6", "ГЕНІЙ", "СЛОВО CHIPS").
 * Text is centred in a fixed row of modules; a change rolls left to right.
 */
export function FlapStrip({ text, tone, maxWidth }: FlapStripProps) {
  const chars = text.toUpperCase().slice(0, CELLS).split("");
  const pad = Math.floor((CELLS - chars.length) / 2);
  const cells = Array.from({ length: CELLS }, (_, i) => chars[i - pad] ?? "");
  // The roll starts at the first module that changes: "СПРОБ 6" → "СПРОБ 5" flips the digit at once.
  const prev = useRef(cells);
  const firstChanged = Math.max(0, cells.findIndex((ch, i) => ch !== prev.current[i]));
  useEffect(() => {
    prev.current = cells;
  });
  return (
    <div
      className="flap-board flap-strip mx-auto grid w-full gap-[3px] p-[5px]"
      style={{ maxWidth, gridTemplateColumns: `repeat(${CELLS}, minmax(0, 1fr))` }}
      role="status"
      aria-label={text}
    >
      {cells.map((ch, i) => (
        <FlapTile
          key={i}
          face={{ ch: ch === " " ? "" : ch, s: ch && ch !== " " ? tone : null }}
          mode="type"
          delay={Math.max(0, i - firstChanged) * STEP_MS}
          alphabet={STRIP_ALPHABET}
        />
      ))}
    </div>
  );
}
